import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import { articleList } from "@/mocks/articles";
import { articleCategories, siteInfo } from "@/mocks/site";
import type { Article } from "@/types";

export default function Articles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "all";

  const filtered = useMemo(
    () =>
      (categoryParam === "all"
        ? articleList
        : articleList.filter((item) => item.category === categoryParam)) as Article[],
    [categoryParam]
  );

  const [featured, ...rest] = filtered;

  const allTags = useMemo(() => {
    const set = new Set<string>();
    articleList.forEach((item) => item.tags.forEach((tag) => set.add(tag)));
    return Array.from(set).slice(0, 10);
  }, []);

  useSeo({
    title: "资讯专栏 - 新番速递与制作幕后 | 樱花动漫社",
    description:
      "樱花动漫社资讯专栏，收录新番速递、制作幕后、作品解析与观看指南四类长文，用编辑视角带你看懂动画的叙事、节奏与配乐。",
    keywords: "动漫资讯,新番速递,动画解析,制作幕后,观看指南",
    path: "/articles",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "资讯专栏",
        url: `${siteInfo.domain}/articles`,
        description: "新番速递、制作幕后、作品解析与观看指南专栏集合。",
        inLanguage: "zh-CN",
        isPartOf: { "@type": "WebSite", name: siteInfo.name, url: siteInfo.domain },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.domain },
          { "@type": "ListItem", position: 2, name: "资讯专栏", item: `${siteInfo.domain}/articles` },
        ],
      },
    ],
  });

  const setCategory = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== "all") {
      next.set("category", value);
    } else {
      next.delete("category");
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "资讯专栏" }]} />

      <div className="mt-6 max-w-3xl">
        <h1 className="font-heading text-3xl font-700 tracking-tight text-foreground-950 md:text-4xl">
          资讯专栏
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
          我们在这里讨论动画本身：新一季有哪些作品值得留意、一部作品是怎么被制作出来的、
          为什么某个镜头会让你放松。文章偏长，适合配一杯茶慢慢读。
        </p>
      </div>

      {/* 分类筛选 */}
      <div className="mt-8 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setCategory("all")}
          className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
            categoryParam === "all"
              ? "bg-primary-500 text-background-50"
              : "bg-background-100 text-foreground-700 hover:bg-background-200"
          }`}
        >
          全部
        </button>
        {articleCategories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => setCategory(cat.id)}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
              categoryParam === cat.id
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px]">
        <section className="min-w-0">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center rounded-xl border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-article-line text-2xl" aria-hidden="true"></i>
              </span>
              <p className="mt-4 font-heading text-base font-600 text-foreground-900">该分类下还没有文章</p>
              <button
                type="button"
                onClick={() => setCategory("all")}
                className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部文章
              </button>
            </div>
          ) : (
            <>
              {featured && <ArticleCard article={featured} variant="feature" />}
              <div className="mt-6 flex flex-col gap-4">
                {rest.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </>
          )}
        </section>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-xl border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-600 text-foreground-950">专栏方向</h2>
            <ul className="mt-4 space-y-2 text-sm">
              {articleCategories.map((cat) => (
                <li key={cat.id}>
                  <button
                    type="button"
                    onClick={() => setCategory(cat.id)}
                    className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-foreground-700 transition-colors hover:bg-background-100"
                  >
                    {cat.name}
                    <span className="text-xs text-foreground-500">
                      {articleList.filter((item) => item.category === cat.id).length}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 rounded-xl border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-600 text-foreground-950">热门标签</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {allTags.map((tag) => (
                <span key={tag} className="rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-900">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-6 rounded-xl bg-secondary-50 p-5">
            <h2 className="font-heading text-base font-600 text-foreground-950">想直接挑作品？</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              前往作品库按分类浏览，或查看编辑精选榜获取推荐。
            </p>
            <Link
              to="/works"
              className="mt-4 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-500 text-background-50 transition-colors hover:bg-secondary-600"
            >
              <i className="ri-stack-line" aria-hidden="true"></i>
              进入作品库
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}